import { AgGridReact } from "ag-grid-react";
import { FilterChips } from "components";
import { FilterItem } from "models/types";
import { ComponentRef, forwardRef, useImperativeHandle, useState } from "react";

type Props = {
  gridRef: React.RefObject<AgGridReact<any>>;
  onFilterCleared?: () => void;
  setParentFilterItems?: (items: FilterItem[]) => void;
};

type FilterChipHandle = {
  onFilterApplied(): void;
};

export const FilterChipComponent = forwardRef<FilterChipHandle, Props>(
  (props, ref) => {
    const { gridRef, onFilterCleared, setParentFilterItems } = props;
    const [filterItems, setFilterItems] = useState<FilterItem[]>([]);

    // The component instance will be extended
    // with whatever you return from the callback passed
    // as the second argument
    useImperativeHandle(ref, () => ({
      onFilterApplied() {
        const filtersApplied = gridRef.current?.api.getFilterModel();
        if (filtersApplied) {
          const items: FilterItem[] = new Array<FilterItem>();
          Object.keys(filtersApplied).forEach((key: any) => {
            const column: any = gridRef.current?.api?.getColumn(key);
            const displayName: any =
              gridRef.current?.api?.getDisplayNameForColumn(column, "model");

            items.push({
              field: key,
              header: displayName ?? key,
              filter: filtersApplied[key]
            });
          });
          setFilterItems(items);
          setParentFilterItems?.(items);
        }
      }
    }));

    const onCategoryDelete = (item: FilterItem) => {
      gridRef.current?.api.destroyFilter(item.field);
      onFilterCleared?.();
    };

    const onChipDelete = (item: { field: string; value: string }) => {
      const filterModel = gridRef.current?.api.getFilterModel();
      const model = filterModel ? filterModel[item.field] : null;
      if (model) {
        const values = model.values.filter((val: any) => val !== item.value);
        if (values.length === 0) {
          gridRef.current?.api.destroyFilter(item.field);
          onFilterCleared?.();
          return;
        }
        gridRef.current?.api?.setColumnFilterModel(item.field, {
          ...model,
          values
        });
        gridRef.current?.api.onFilterChanged();
        onFilterCleared?.();
      }
    };

    const resetFilters = () => {
      gridRef.current?.api.setFilterModel(null);
      onFilterCleared?.();
    };

    if (filterItems && filterItems.length)
      return (
        <FilterChips
          items={filterItems}
          onChipDelete={onChipDelete}
          onCategoryDelete={onCategoryDelete}
          resetAll={resetFilters}
        />
      );
    return null;
  }
);

export type FilterChipComponentRef = ComponentRef<typeof FilterChipComponent>;
